import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useFetchStagesHistory } from "../../hooks/api/stagesHistoryApiHooks";
import { formatDateHour, formatMachineDate } from "../../services/date";
import { ApiDataContext } from "../../contexts/ApiDataContext";
import CustomLoadingButton from "../shared/button/CustomLoadingButton";
import {
  updateFinishedActivity,
  updateStartedActivity,
  updateCanceledActivity,
} from "../../services/api/api";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

/**
 * Drawer unificado com duas áreas: à esquerda o formulário de finalização e à direita o histórico.
 * Mantém a mesma lógica de negócios dos modais anteriores, mas em uma experiência única.
 */
export default function ActivityActionDrawer({ open, onClose, activity }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userName = user?.userName;

  const queryClient = useQueryClient();
  const [isActivityComplete, setIsActivityComplete] = useState(true);
  const [isAddObsMarked, setIsAddObsMarked] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [cancelRequestLoading, setCancelRequestLoading] = useState(false);
  const [showForm, setShowForm] = useState(false); // formulário fechado por padrão

  const projectId = activity?.id;
  const providenceHistoryId = activity?.providenceHistoryId;
  const stageHistoryId = activity?.stageHistoryId;

  const { stagesHistoryLoading, stagesHistoryData = [] } = useFetchStagesHistory(projectId);
  const { apiData } = useContext(ApiDataContext);

  // Form state usando useState direto (sem hooks customizados problemáticos)
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedProvidence, setSelectedProvidence] = useState(null);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [observation, setObservation] = useState("");

  // Ao abrir o drawer pela primeira vez de uma atividade ainda não em realização, marcar início
  useEffect(() => {
    async function markStartedIfNeeded() {
      if (!open || !activity) return;
      try {
        if (activity.beingRealized !== 1 && providenceHistoryId && userName) {
          await updateStartedActivity(providenceHistoryId, userName);
        }
      } catch (_) {
        // Sem tratamento especial; manter UX fluida
      }
    }
    markStartedIfNeeded();
    return () => {};
  }, [open, activity, providenceHistoryId, userName]);

  // Inicializar formulário quando atividade mudar
  useEffect(() => {
    if (!activity) return;
    
    // Encontrar stage e providence nos dados da API
    const initialStage = apiData?.stages?.find(s => s.id === activity.stageId) || null;
    const initialProvidence = apiData?.providences?.find(p => p.id === activity.providenceId) || null;
    
    setSelectedStage(initialStage);
    setSelectedProvidence(initialProvidence);
    setSelectedDeadline(activity.dueDate ? dayjs(formatMachineDate(activity.dueDate)) : null);
    setObservation(activity.note || "");
    setIsActivityComplete(true);
    setIsAddObsMarked(false);
    setShowForm(false);
  }, [activity, apiData]);

  const handleFinish = async () => {
    try {
      if (!isActivityComplete || !selectedStage || !selectedProvidence) return;
      setRequestLoading(true);
      await updateFinishedActivity(
        projectId,
        providenceHistoryId,
        stageHistoryId,
        selectedDeadline,
        selectedStage.id,
        selectedProvidence.id,
        observation
      );
      queryClient.invalidateQueries({ queryKey: ["userProvidences"] });
      queryClient.invalidateQueries({ queryKey: ["homeBaseQuery"] });
      onClose();
    } catch (_) {
      // manter UX simples
    } finally {
      setRequestLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancelRequestLoading(true);
      await updateCanceledActivity(providenceHistoryId);
      queryClient.invalidateQueries({ queryKey: ["userProvidences"] });
      queryClient.invalidateQueries({ queryKey: ["homeBaseQuery"] });
      onClose();
    } catch (_) {
      // manter UX simples
    } finally {
      setCancelRequestLoading(false);
    }
  };

  const leftTitle = useMemo(() => "Finalizar Atividade", []);
  const rightTitle = useMemo(() => "Histórico de Providências", []);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: showForm ? 1120 : 680 }, transition: "width 0.3s ease" } }}>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, height: "100%", overflow: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "1.2rem", fontWeight: 700 }}>
            Projeto {activity?.projectNumber}
          </Typography>
          {activity?.providenceInitialDate ? (
            <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
              Iniciou em {" "}
              {formatDateHour(activity?.providenceInitialDate)}
            </Typography>
          ) : null}
        </Box>

        <Divider />

        <Grid container spacing={3} sx={{ flex: 1 }}>
          {/* Formulário (esquerda) */}
          {showForm && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontWeight: 700 }}>{leftTitle}</Typography>
                  <IconButton size="small" onClick={() => setShowForm(false)} sx={{ ml: "auto" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Typography sx={{ width: "30%", fontWeight: 500 }}>Nome</Typography>
                  <Typography sx={{ width: "70%" }}>{activity?.name}</Typography>
                </Box>
                <Divider />

                <Box sx={{ display: "flex" }}>
                  <Typography sx={{ width: "30%", fontWeight: 500 }}>Providência</Typography>
                  <Typography sx={{ width: "70%" }}>{activity?.providenceName}</Typography>
                </Box>
                <Divider />

                <Box sx={{ display: "flex" }}>
                  <Typography sx={{ width: "30%", fontWeight: 500 }}>Etapa</Typography>
                  <Typography sx={{ width: "70%" }}>{activity?.stageName}</Typography>
                </Box>
                <Divider />

                <FormControlLabel
                  control={<Switch checked={isActivityComplete} onChange={() => setIsActivityComplete(!isActivityComplete)} />}
                  label="Atividade Completa?"
                  labelPlacement="start"
                  sx={{ justifyContent: "space-between", m: 0 }}
                />

                <Autocomplete
                  fullWidth
                  disableClearable
                  disabled={!isActivityComplete}
                  options={apiData?.stages || []}
                  value={selectedStage}
                  onChange={(event, newValue) => setSelectedStage(newValue)}
                  isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    return option.id === value.id;
                  }}
                  getOptionLabel={(option) => (option ? option.stageName || "" : "")}
                  renderInput={(params) => <TextField {...params} label="Próxima Etapa" />}
                />

                <Autocomplete
                  fullWidth
                  disableClearable
                  disabled={!isActivityComplete}
                  options={apiData?.providences || []}
                  value={selectedProvidence}
                  onChange={(event, newValue) => setSelectedProvidence(newValue)}
                  isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    return option.id === value.id;
                  }}
                  getOptionLabel={(option) => (option ? option.providenceName || "" : "")}
                  renderInput={(params) => <TextField {...params} label="Próxima Providência" />} 
                />

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MuiDatePicker
                    label="Prazo Máximo"
                    format="DD/MM/YYYY"
                    disabled={!isActivityComplete}
                    value={selectedDeadline}
                    onChange={(newValue) => setSelectedDeadline(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>

                <FormControlLabel
                  control={<Switch checked={isAddObsMarked && isActivityComplete} onChange={() => setIsAddObsMarked(!isAddObsMarked)} disabled={!isActivityComplete} />}
                  label="Adicionar Observação?"
                  labelPlacement="start"
                  sx={{ justifyContent: "space-between", m: 0 }}
                />

                <TextField
                  label="Observação"
                  multiline
                  fullWidth
                  disabled={!isAddObsMarked || !isActivityComplete}
                  maxRows={4}
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                />

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, pt: 1 }}>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={handleCancel}
                    disabled={cancelRequestLoading || requestLoading}
                    sx={{ minWidth: 120 }}
                  >
                    {cancelRequestLoading ? "Cancelando..." : "Cancelar Atividade"}
                  </Button>
                  
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {!isActivityComplete ? (
                      <Button variant="contained" onClick={onClose} sx={{ backgroundColor: "#CA9900" }}>
                        Sair sem finalizar
                      </Button>
                    ) : (
                      <>
                        <Button variant="text" color="inherit" onClick={onClose}>
                          Voltar
                        </Button>
                        <CustomLoadingButton
                          buttonText="Finalizar e Avançar"
                          buttonLoadingText="Finalizando..."
                          sx={{}}
                          disabled={requestLoading || cancelRequestLoading}
                          handleButtonClick={handleFinish}
                          requestLoading={requestLoading}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Histórico */}
          <Grid item xs={12} md={showForm ? 6 : 12}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>{rightTitle}</Typography>
                {!showForm && (
                  <IconButton 
                    onClick={() => setShowForm(true)}
                    size="small"
                    sx={{ 
                      bgcolor: "#CA9900",
                      color: "white",
                      width: 32,
                      height: 32,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "#B68700",
                        boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
                        transform: "scale(1.05)"
                      },
                      "&:active": {
                        transform: "scale(0.95)"
                      }
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
              {stagesHistoryLoading ? (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List sx={{ px: 0 }}>
                  {stagesHistoryData.length === 0 ? (
                    <Box sx={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      alignItems: "center", 
                      justifyContent: "center",
                      py: 8,
                      gap: 1,
                      color: "text.secondary"
                    }}>
                      <HistoryIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                      <Typography sx={{ fontWeight: 600 }}>Nenhum histórico</Typography>
                      <Typography variant="body2">Esta atividade ainda não tem providências registradas.</Typography>
                    </Box>
                  ) : (
                    stagesHistoryData.map((stageItem, index) => {
                      const parsedInitialDate = formatDateHour(stageItem.initialDate);
                      const parsedFinalDate = stageItem.finalDate ? formatDateHour(stageItem.finalDate) : "";
                      const isCurrent = !stageItem.finalDate;
                      const isLast = index === stagesHistoryData.length - 1;
                      
                      return (
                        <ListItem 
                          key={index}
                          sx={{ 
                            p: 2.5, 
                            border: "1px solid #e0e0e0", 
                            borderRadius: 2, 
                            mb: isLast ? 0 : 2,
                            bgcolor: isCurrent ? "#FFF8E1" : "white", 
                            borderLeft: isCurrent ? "4px solid #CA9900" : "1px solid #e0e0e0",
                            boxShadow: isCurrent ? 2 : 0,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: 2
                            }
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: "1rem", mb: 0.5 } }}
                            primary={stageItem.stages?.stageName}
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                                  {parsedInitialDate}{parsedFinalDate ? ` — ${parsedFinalDate}` : " — Em andamento"}
                                </Typography>
                                {stageItem.note ? (
                                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, fontStyle: "italic" }}>
                                    {stageItem.note}
                                  </Typography>
                                ) : null}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })
                  )}
                </List>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}


